import { inject, Injectable } from '@angular/core';
import { IFormConfig, IFormConfigGroup, IFormConfigList } from '@shared/configurable/models/IFormConfig';
import { EFormConfig } from '@shared/enums/EFormConfig';
import { BehaviorSubject, Observable } from 'rxjs';
import { FormSettingsApiService } from '@api/form-settings/form-settings-api.service';
import { IApiResponse } from '@shared/models/IApiResponse';
import { AFFILIATES_FIELDS } from '@shared/configurable/fields/affiliates.fields';
import { AGENTS_FIELDS } from '@shared/configurable/fields/agents.fields';
import { keysWith, toFormDefaults } from '@shared/configurable/field-registry';
import { CONTACTS_FIELDS } from '@shared/configurable/fields/contacts.fields';
import { INSURANCE_FIELDS } from '@shared/configurable/fields/insurance.fields';
import { INSURANCE_FINANCE_FIELDS } from '@shared/configurable/fields/insurance-finance.fields';
import { OBJECT_FIELDS } from '@shared/configurable/fields/object.fields';
import { OBJECT_TARIFF_FIELDS } from '@shared/configurable/fields/object-tariff.fields';
import { INSURED_FIELDS } from '@shared/configurable/fields/insured.fields';
import { CLAIM_FIELDS } from '@shared/configurable/fields/claim.fields';
import { StorageService } from '@shared/service/storage.service';

const FORM_CONFIGS_STORAGE_KEY = 'formConfigs';

/**
 * Fills gaps in column indices (so the CSS grid always has a consistent number of
 * cells) and sorts groups, rows and columns by their index. Mutates and returns `groups`.
 * Shared by {@link FormConfigService.getConfigForTemplate} and the form configurator editor.
 */
export function normalizeFormLayout(groups: IFormConfigGroup[]): IFormConfigGroup[] {
  for (const group of groups) {
    for (const row of group.rows) {
      const maxCol = Math.max(...row.columns.map(c => c.column));
      for (let i = 1; i <= maxCol; i++) {
        if (!row.columns.find(c => c.column === i)) {
          row.columns.push({ column: i, fields: [] });
        }
      }
      row.columns.sort((a, b) => a.column - b.column);
    }
    group.rows.sort((a, b) => a.row - b.row);
  }
  return groups.sort((a, b) => a.group - b.group);
}

/**
 * Fields that cannot have their `required` flag toggled by the user in the form editor.
 * These are always required regardless of configuration.
 */
export const LOCKED_REQUIRED_FIELDS: Partial<Record<EFormConfig, string[]>> = {
  [EFormConfig.Insured]: keysWith(INSURED_FIELDS, 'lockedRequired'),
  [EFormConfig.Insurance]: keysWith(INSURANCE_FIELDS, 'lockedRequired'),
  [EFormConfig.InsuranceFinance]: keysWith(INSURANCE_FINANCE_FIELDS, 'lockedRequired'),
  [EFormConfig.Claim]: keysWith(CLAIM_FIELDS, 'lockedRequired'),
  [EFormConfig.Contacts]: keysWith(CONTACTS_FIELDS, 'lockedRequired'),
  [EFormConfig.Affiliates]: keysWith(AFFILIATES_FIELDS, 'lockedRequired'),
  [EFormConfig.Agents]: keysWith(AGENTS_FIELDS, 'lockedRequired'),
  [EFormConfig.Object]: keysWith(OBJECT_FIELDS, 'lockedRequired'),
  [EFormConfig.ObjectTariff]: keysWith(OBJECT_TARIFF_FIELDS, 'lockedRequired'),
};

/**
 * Fields excluded from the `required` feature entirely — they can appear in the form
 * but the "mandatory" toggle has no effect on them.
 */
export const DISABLE_REQUIRED_FEATURE_FIELDS: Partial<Record<EFormConfig, string[]>> = {
  [EFormConfig.Insured]: keysWith(INSURED_FIELDS, 'noRequiredToggle'),
  [EFormConfig.Insurance]: keysWith(INSURANCE_FIELDS, 'noRequiredToggle'),
  [EFormConfig.Claim]: keysWith(CLAIM_FIELDS, 'noRequiredToggle'),
  [EFormConfig.Contacts]: keysWith(CONTACTS_FIELDS, 'noRequiredToggle'),
  [EFormConfig.Affiliates]: keysWith(AFFILIATES_FIELDS, 'noRequiredToggle'),
  [EFormConfig.InsuranceFinance]: keysWith(INSURANCE_FINANCE_FIELDS, 'noRequiredToggle'),
  [EFormConfig.Agents]: keysWith(AGENTS_FIELDS, 'noRequiredToggle'),
};

/**
 * Fields whose label in the form configurator is the raw `controlName` instead of a
 * translated caption (tenant-customisable fields like segmentations and checks).
 */
export const CONTROL_NAME_IN_CONFIGURATOR_FIELDS: Partial<Record<EFormConfig, string[]>> = {
  [EFormConfig.Object]: keysWith(OBJECT_FIELDS, 'useControlNameInConfigurator'),
  [EFormConfig.ObjectTariff]: keysWith(OBJECT_TARIFF_FIELDS, 'useControlNameInConfigurator'),
};

@Injectable()
export class FormConfigService {
  private readonly _api = inject(FormSettingsApiService);
  private readonly _storage = inject(StorageService);

  /**
   * Fallback layout used when the server hasn't returned a custom config yet
   * and localStorage is empty. Reflects the out-of-the-box field arrangement.
   *
   * Convention: entries with row:0 / column:0 are "hidden by default" —
   * they're available for admins to configure into the layout but won't
   * appear unless explicitly placed (getConfigForTemplate filters row === 0).
   */
  private readonly _formConfigsDefault: IFormConfigList = {
    [EFormConfig.Insured]: toFormDefaults(EFormConfig.Insured, INSURED_FIELDS),
    [EFormConfig.Insurance]: toFormDefaults(EFormConfig.Insurance, INSURANCE_FIELDS),
    [EFormConfig.Claim]: toFormDefaults(EFormConfig.Claim, CLAIM_FIELDS),
    [EFormConfig.Object]: toFormDefaults(EFormConfig.Object, OBJECT_FIELDS),
    [EFormConfig.ObjectTariff]: toFormDefaults(EFormConfig.ObjectTariff, OBJECT_TARIFF_FIELDS),
    [EFormConfig.Contacts]: toFormDefaults(EFormConfig.Contacts, CONTACTS_FIELDS),
    [EFormConfig.InsuranceFinance]: toFormDefaults(EFormConfig.InsuranceFinance, INSURANCE_FINANCE_FIELDS),
    [EFormConfig.Affiliates]: toFormDefaults(EFormConfig.Affiliates, AFFILIATES_FIELDS),
    [EFormConfig.Agents]: toFormDefaults(EFormConfig.Agents, AGENTS_FIELDS),
  };

  // Persisted in localStorage so the layout survives a page refresh without a server round-trip.
  // Starts as null when nothing is stored yet; fetchFormConfigs() or initFormConfigsFromStorage() fill it.
  private readonly _formConfigs = new BehaviorSubject<IFormConfigList>(this._storage.get<IFormConfigList>(FORM_CONFIGS_STORAGE_KEY));
  readonly formConfigs$ = this._formConfigs.asObservable();

  setFormConfigs(configs: IFormConfigList): void {
    this._formConfigs.next(configs);
  }

  updateConfig(formConfigs: IFormConfig[], entity: EFormConfig): Observable<IApiResponse<any>> {
    const updated: IFormConfigList = { ...this._formConfigs.getValue(), [entity]: formConfigs };
    this.setFormConfigs(updated);
    this._storage.set(FORM_CONFIGS_STORAGE_KEY, updated);
    return this._api.updateSettings(updated[entity]);
  }

  getFormConfigs(entity: EFormConfig): IFormConfig[] {
    const configs = this._formConfigs.getValue();
    return configs?.[entity] ?? [];
  }

  /** Whether a field (by config key) is marked required (mandatory) in the saved layout for the entity. */
  isFieldRequired(entity: EFormConfig, controlName: string): boolean {
    return this.getFormConfigs(entity).some(item => item.controlName === controlName && !!item.required);
  }

  getFormConfigsDefault(entity: EFormConfig): IFormConfig[] {
    return (this._formConfigsDefault[entity] ?? []).map(f => ({ ...f, required: false }));
  }

  /**
   * Transforms a flat IFormConfig[] into the nested Group → Row → Column → Field
   * structure consumed by DynamicFormComponent.
   *
   * Fields with row === 0 are "hidden" (available in the config editor but not
   * rendered by default) and are stripped from the output.
   */
  getConfigForTemplate(entity: EFormConfig): IFormConfigGroup[] {
    const groups: IFormConfigGroup[] = [];

    for (const item of this.getFormConfigs(entity)) {
      let group = groups.find(g => g.group === item.group);
      if (!group) {
        group = { group: item.group, rows: [] };
        groups.push(group);
      }

      let row = group.rows.find(r => r.row === item.row);
      if (!row) {
        row = { row: item.row, columns: [] };
        group.rows.push(row);
      }

      let col = row.columns.find(c => c.column === item.column);
      if (!col) {
        col = { column: item.column, fields: [] };
        row.columns.push(col);
      }

      col.fields.push({ order: item.order, controlName: item.controlName, required: item.required });
      col.fields.sort((a, b) => a.order - b.order);
    }

    // Fill column gaps + sort (shared with the form configurator), then drop the "hidden" row 0.
    return normalizeFormLayout(groups).map(group => ({ ...group, rows: group.rows.filter(r => r.row !== 0) }));
  }

  fetchFormConfigs(): void {
    this._api.getSettings().subscribe(res => {
      if (res.data) {
        this.sortFormConfigs(res.data);
      }
    });
  }

  initFormConfigsFromStorage(): void {
    const stored = this._storage.get<IFormConfigList>(FORM_CONFIGS_STORAGE_KEY);
    if (stored) {
      this.setFormConfigs(stored);
    } else {
      this.fetchFormConfigs();
    }
  }

  private sortFormConfigs(formConfigs: IFormConfig[]): void {
    const result: IFormConfigList = {
      [EFormConfig.Insured]: [],
      [EFormConfig.Insurance]: [],
      [EFormConfig.Claim]: [],
      [EFormConfig.Object]: [],
      [EFormConfig.ObjectTariff]: [],
      [EFormConfig.Contacts]: [],
      [EFormConfig.InsuranceFinance]: [],
      [EFormConfig.Affiliates]: [],
      [EFormConfig.Agents]: [],
    };

    for (const item of formConfigs) {
      result[item.formType].push(item);
    }

    this.setFormConfigs(result);
    this._storage.set(FORM_CONFIGS_STORAGE_KEY, result);
  }
}
